import { ChipCellRenderer } from "@/controls/ag-grid/components/ChipCellRenderer";
import { dxtToLocalServerTime } from "@dexteel/mesf-core";
import { Button, Grid2 as Grid } from "@mui/material";
import { ColDef, ICellRendererParams } from "ag-grid-enterprise";
import moment from "moment";

const applyCommonColumnSettings = (column: ColDef): ColDef => {
	return {
		...column,
		sortable: true,
		wrapText: true,
		autoHeight: true,
		wrapHeaderText: true,
	};
};

export const useTableData = () => {
	const columns: ColDef[] = [
		applyCommonColumnSettings({
			field: "ProcessId",
			headerName: "Process Id",
			minWidth: 60,
			hide: true,
		}),
		applyCommonColumnSettings({
			field: "ProductionDate",
			headerName: "Production Date",
			valueGetter: (params) => {
				return moment(params?.data?.ProductionDate).format("MM/DD/YYYY");
			},
			minWidth: 112,
			flex: 1,
			valueFormatter: (params) => {
				return moment(params?.data?.ProductionDate).format("MM/DD/YYYY");
			},
		}),
		applyCommonColumnSettings({
			field: "Shift",
			headerName: "Shift",
			minWidth: 53,
			maxWidth: 53,
			cellStyle: {
				display: "flex",
				justifyContent: "center",
				alignItems: "center",
			},
			flex: 1,
		}),
		applyCommonColumnSettings({
			field: "Crew",
			headerName: "Crew",
			minWidth: 56,
			maxWidth: 56,
			cellStyle: {
				display: "flex",
				justifyContent: "center",
				alignItems: "center",
			},
			flex: 1,
		}),
		applyCommonColumnSettings({
			field: "HeatId",
			headerName: "Heat Id",
			minWidth: 60,
			hide: true,
		}),
		applyCommonColumnSettings({
			field: "Heat",
			headerName: "Heat",
			minWidth: 70,
			maxWidth: 70,
			flex: 1,
		}),
		applyCommonColumnSettings({
			field: "AssetId",
			headerName: "Asset Id",
			minWidth: 60,
			hide: true,
		}),
		applyCommonColumnSettings({
			field: "Asset",
			headerName: "Asset",
			cellClass: (params) =>
				params.data.AssetId ? `eaf-${params.data.AssetId}` : "",
			cellRenderer: ChipCellRenderer,
			minWidth: 87,
			flex: 1,
		}),
		applyCommonColumnSettings({
			field: "Grade",
			headerName: "Grade",
			minWidth: 96,
			flex: 1,
		}),
		applyCommonColumnSettings({
			field: "Ladle",
			headerName: "Ladle",
			minWidth: 57,
			maxWidth: 57,
			flex: 1,
		}),
		applyCommonColumnSettings({
			field: "EafStart",
			headerName: "Start",
			valueGetter: (params) =>
				dxtToLocalServerTime(`${params.data?.EafStart}`, "HH:mm:ss"),
			minWidth: 82,
			flex: 1,
			valueFormatter: (params) =>
				dxtToLocalServerTime(`${params.data?.EafStart}`, "HH:mm:ss"),
		}),
		applyCommonColumnSettings({
			field: "EafEnd",
			headerName: "End",
			valueGetter: (params) =>
				dxtToLocalServerTime(`${params.data?.EafEnd}`, "HH:mm:ss"),
			minWidth: 82,
			flex: 1,
			valueFormatter: (params) =>
				dxtToLocalServerTime(`${params.data?.EafEnd}`, "HH:mm:ss"),
		}),
		applyCommonColumnSettings({
			field: "TapToTap",
			headerName: "Tap To Tap",
			valueFormatter: (params) => (params.value ? `${params.value} min` : ""),
			cellClass: (params) => (params.value > 200 ? "warning-limit" : ""),
			minWidth: 75,
			flex: 1,
		}),
		applyCommonColumnSettings({
			field: "PowerOn",
			headerName: "Power On",
			valueFormatter: (params) => (params.value ? `${params.value} min` : ""),
			minWidth: 75,
			flex: 1,
		}),
		applyCommonColumnSettings({
			field: "PowerOff",
			headerName: "Power Off",
			valueFormatter: (params) => (params.value ? `${params.value} min` : ""),
			minWidth: 75,
			flex: 1,
		}),
		applyCommonColumnSettings({
			field: "Delays",
			headerName: "Delays",
			valueFormatter: (params) => (params.value ? `${params.value} min` : ""),
			minWidth: 75,
			flex: 1,
		}),
		applyCommonColumnSettings({
			field: "MicroDelays",
			headerName: "Micro Delays",
			valueFormatter: (params) => (params.value ? `${params.value} min` : ""),
			minWidth: 75,
			flex: 1,
		}),
		applyCommonColumnSettings({
			field: "MWH",
			headerName: "MWH",
			minWidth: 57,
			cellStyle: {
				display: "flex",
				justifyContent: "flex-end",
			},
			flex: 1,
		}),
		applyCommonColumnSettings({
			field: "OxygenVolume",
			headerName: "O2 Vol [scf]",
			valueFormatter: (params) => (params.value ? params.value.toFixed(1) : ""),
			minWidth: 78,
			cellStyle: {
				display: "flex",
				justifyContent: "flex-end",
			},
			flex: 1,
		}),
		applyCommonColumnSettings({
			field: "Charges",
			headerName: "Charges",
			minWidth: 60,
			maxWidth: 60,
			flex: 1,
		}),
		applyCommonColumnSettings({
			field: "Scrap",
			headerName: "Scrap",
			minWidth: 80,
			flex: 1,
		}),
		applyCommonColumnSettings({
			field: "LastTemperature",
			headerName: "Last Temperature",
			minWidth: 122,
			flex: 1,
		}),
		applyCommonColumnSettings({
			field: "ShiftStart",
			headerName: "Shift Start",
			valueGetter: (params) =>
				dxtToLocalServerTime(`${params.data?.ShiftStart}`, "HH:mm:ss"),
			minWidth: 82,
			flex: 1,
			valueFormatter: (params) =>
				dxtToLocalServerTime(`${params.data?.ShiftStart}`, "HH:mm:ss"),
		}),
		applyCommonColumnSettings({
			field: "ShiftEnd",
			headerName: "Shift End",
			valueGetter: (params) =>
				dxtToLocalServerTime(`${params.data?.ShiftEnd}`, "HH:mm:ss"),
			minWidth: 82,
			flex: 1,
			valueFormatter: (params) =>
				dxtToLocalServerTime(`${params.data?.ShiftEnd}`, "HH:mm:ss"),
		}),
		applyCommonColumnSettings({
			field: "manual",
			headerName: "Manual",
			minWidth: 68,
			maxWidth: 68,
			valueGetter: (params) => (params.data?.manual ? "Yes" : "No"),
			valueFormatter: (params) => (params.value ? "Yes" : "No"),
			cellRenderer: (params: ICellRendererParams) => {
				return params.data?.manual ? (
					<Grid
						container
						justifyContent="center"
						style={{ width: "100%", height: "100%" }}
					>
						<Grid size={{ md: 12 }}>
							<Button
								title={params.data?.manual ? "Manual" : "Automatic"}
								variant={"contained"}
								color={"primary"}
								style={{
									cursor: "auto",
									backgroundColor: params.data?.manual ? "primary" : "green",
									minWidth: "1px",
									padding: "1px 7px",
								}}
							>
								{params.data?.manual ? "M" : "A"}
							</Button>
						</Grid>
					</Grid>
				) : null;
			},
			cellStyle: {
				display: "flex",
				justifyContent: "center",
				alignItems: "center",
			},
		}),
	];

	return { columns };
};
